import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Radio, DollarSign, TrendingUp, Monitor, Play, Signal, UserCircle,
  Copy, ChevronRight, BarChart3, Wallet, Clock, Film, Zap, LogOut, User,
  Settings,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';

function StatusBadge({ status }) {
  const map = { pending:'bg-gray-500/10 text-gray-400 border-gray-500/20', approved:'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', rejected:'bg-red-500/10 text-red-500 border-red-500/20', active:'bg-blue-500/10 text-blue-400 border-blue-500/20', playing:'bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/20', holding:'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', ended:'bg-gray-500/10 text-gray-400 border-gray-500/20', cancelled:'bg-gray-500/10 text-gray-500 border-gray-500/20' };
  return <Badge className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider font-mono border ${map[status]||map.pending}`}>{status}</Badge>;
}

export default function StreamerHome() {
  const { user, token, logout, API } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [triggerHistory, setTriggerHistory] = useState([]);
  const [incomingBids, setIncomingBids] = useState([]);
  const [profileAnalytics, setProfileAnalytics] = useState(null);
  const [breakHistory, setBreakHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [ccvData, setCcvData] = useState(null);
  const [linkingPlatform, setLinkingPlatform] = useState('');
  const [linkingChannelId, setLinkingChannelId] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  // WebSocket to track overlay connection
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl) return;
    const ws = new WebSocket(`${wsUrl}/api/ws/triggers`);
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    return () => ws.close();
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [profRes, earnRes, placeRes, trigRes, bidsRes, analyticsRes, breaksRes, ccvRes] = await Promise.all([
        axios.get(`${API}/streamer-profiles/me`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/earnings/summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/placements/streamer-queue`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/ads/trigger-history`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/placements/incoming`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/streamer-profiles/my/analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/ad-breaks/history`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/ccv/platforms`, { headers }).catch(() => ({ data: null })),
      ]);
      setProfile(profRes.data);
      setEarnings(earnRes.data);
      setPlacements(placeRes.data);
      setTriggerHistory(trigRes.data);
      setIncomingBids(bidsRes.data);
      setProfileAnalytics(analyticsRes.data);
      setBreakHistory(breaksRes.data);
      setCcvData(ccvRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const obsUrl = `${window.location.origin}/obs-overlay`;
  const profileComplete = profile ? (
    (profile.displayName ? 1 : 0) + (profile.avgCCV ? 1 : 0) + (profile.platforms?.length ? 1 : 0) +
    (profile.categories?.length ? 1 : 0) + (profile.bio ? 1 : 0)
  ) : 0;
  const profilePercent = Math.round((profileComplete / 5) * 100);

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#050505]">
        {/* Header */}
        <header className="border-b border-white/10 bg-[#0a0a0a]">
          <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#FF3B30] flex items-center justify-center"><Radio className="w-4 h-4 text-white" /></div>
              <span className="font-heading text-xl font-black tracking-tighter text-white uppercase">AYO<span className="text-gray-500 ml-1 font-medium text-sm normal-case">source</span></span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-button" variant="ghost" className="flex items-center gap-2 text-gray-300 hover:text-white hover:bg-white/5">
                  <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center"><User className="w-3.5 h-3.5" /></div>
                  <span className="text-sm font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-[#121212] border-white/10">
                <DropdownMenuLabel className="text-gray-400">
                  <p className="text-sm text-white font-medium">{user?.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="logout-button" className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer" onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-6 py-8">
          {/* Welcome */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-white uppercase">
              Hey, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-gray-500 text-sm mt-1">Your streaming dashboard. Manage ads, track earnings, go live.</p>
          </div>

          {/* Top Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Balance</span>
                <Wallet className="w-4 h-4 text-emerald-400" />
              </div>
              <p className="text-2xl font-bold text-white font-mono">${(earnings?.availableBalance || 0).toFixed(2)}</p>
            </div>
            <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">This Month</span>
                <TrendingUp className="w-4 h-4 text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-white font-mono">${(earnings?.monthNet || 0).toFixed(2)}</p>
            </div>
            <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Impressions</span>
                <BarChart3 className="w-4 h-4 text-[#FF3B30]" />
              </div>
              <p className="text-2xl font-bold text-white font-mono">{earnings?.totalImpressions || 0}</p>
            </div>
            <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Active Ads</span>
                <Film className="w-4 h-4 text-[#7C3AED]" />
              </div>
              <p className="text-2xl font-bold text-white font-mono">{placements.length}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Quick Actions + Profile */}
            <div className="lg:col-span-2 space-y-6">
              {/* Go Live Card */}
              <button
                data-testid="go-live-button"
                onClick={() => navigate('/streamer')}
                className="w-full border border-[#FF3B30]/20 rounded-lg bg-[#FF3B30]/[0.03] p-6 text-left group hover:border-[#FF3B30]/40 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#FF3B30] rounded-lg flex items-center justify-center shadow-[0_0_30px_rgba(255,59,48,0.2)] group-hover:shadow-[0_0_50px_rgba(255,59,48,0.4)] transition-all">
                      <Play className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white uppercase">Open Stream Console</h3>
                      <p className="text-sm text-gray-500">Trigger ads live with one click</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-[#FF3B30] transition-colors" />
                </div>
                {placements.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-4">
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-mono text-xs">
                      {placements.length} ads ready
                    </Badge>
                    <span className="text-xs text-gray-500 font-mono">
                      Top CPM: ${Math.max(...placements.map(p => p.bidCpm || 0)).toFixed(2)}
                    </span>
                  </div>
                )}
              </button>

              {/* Profile Completion */}
              <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-base font-bold text-white">Profile Completion</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Complete your profile so brands can discover you</p>
                  </div>
                  <span className="text-lg font-bold text-white font-mono">{profilePercent}%</span>
                </div>
                <Progress value={profilePercent} className="h-2 bg-white/5 mb-4" />
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {[
                    { label: 'Name', done: !!profile?.displayName },
                    { label: 'CCV', done: !!profile?.avgCCV },
                    { label: 'Platforms', done: !!profile?.platforms?.length },
                    { label: 'Categories', done: !!profile?.categories?.length },
                    { label: 'Bio', done: !!profile?.bio },
                  ].map((item, i) => (
                    <div key={i} className={`text-center p-2 rounded border ${item.done ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-white/5 bg-white/[0.02]'}`}>
                      <p className={`text-[10px] font-mono uppercase tracking-wider ${item.done ? 'text-emerald-400' : 'text-gray-600'}`}>{item.label}</p>
                    </div>
                  ))}
                </div>
                {profilePercent < 100 && (
                  <Button data-testid="complete-profile-button" onClick={() => navigate('/streamer/profile')}
                    className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm">
                    <UserCircle className="w-4 h-4 mr-2" /> Complete Profile
                  </Button>
                )}
                {profilePercent === 100 && (
                  <Button data-testid="edit-profile-button" variant="ghost" onClick={() => navigate('/streamer/profile')}
                    className="w-full text-gray-400 hover:text-white hover:bg-white/5 text-sm">
                    <Settings className="w-4 h-4 mr-2" /> Edit Profile
                  </Button>
                )}
              </div>

              {/* Ad Queue Management */}
              <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-base font-bold text-white">Your Ad Queue</h3>
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-mono text-[10px]">{placements.length} ads ready</Badge>
                </div>
                {placements.length === 0 ? (
                  <div className="text-center py-6 text-gray-600">
                    <Film className="w-6 h-6 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No ads in your queue</p>
                    <p className="text-xs text-gray-700 mt-1">Accept bids from brands to add ads to your queue</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {placements.map((p, idx) => {
                      const isVideo = p.adMimeType?.startsWith('video/');
                      const pFullUrl = p.adUrl?.startsWith('http') ? p.adUrl : `${process.env.REACT_APP_BACKEND_URL}${p.adUrl}`;
                      return (
                        <div key={p.id} className="flex items-center gap-3 p-3 bg-white/[0.02] rounded border border-white/5">
                          <span className="text-xs font-mono text-gray-600 w-5 text-right">{idx + 1}</span>
                          <div className="w-14 h-9 bg-black rounded overflow-hidden flex-shrink-0">
                            {isVideo ? <video src={pFullUrl} className="w-full h-full object-cover" muted preload="metadata" /> : <img src={pFullUrl} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{p.adFilename}</p>
                            <p className="text-[10px] text-gray-500 font-mono">{p.brandName} &middot; {p.slotDuration || 15}s</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className="text-sm font-bold text-emerald-400 font-mono">${(p.slotPrice || 0).toFixed(2)}</span>
                            <p className="text-[9px] text-gray-600 font-mono">{p.slotsRemaining || '?'} slots left</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Recent Triggers */}
              <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-base font-bold text-white">Recent Ad Triggers</h3>
                  <span className="text-xs text-gray-500 font-mono">{triggerHistory.length} total</span>
                </div>
                {triggerHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No ads triggered yet. Open the stream console to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {triggerHistory.slice(0, 5).map(t => (
                      <div key={t.id} className="flex items-center justify-between p-3 bg-white/[0.02] rounded border border-white/5">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${t.completed ? 'bg-emerald-500' : 'bg-yellow-500'}`} />
                          <div>
                            <p className="text-sm text-white">{t.ad?.filename || 'Ad'}</p>
                            <p className="text-xs text-gray-600 font-mono">{new Date(t.triggeredAt).toLocaleString()}</p>
                          </div>
                        </div>
                        {t.earnings > 0 && (
                          <span className="text-sm font-bold text-emerald-400 font-mono">+${t.earnings.toFixed(4)}</span>
                        )}
                      </div>
                    ))}
                    {triggerHistory.length > 5 && (
                      <button onClick={() => navigate('/streamer/earnings')} className="text-xs text-gray-500 hover:text-white font-mono mt-2">
                        View all {triggerHistory.length} triggers →
                      </button>
                    )}
                  </div>
                )}
              </div>
              {/* Incoming Bids */}
              <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-base font-bold text-white">Incoming Bids</h3>
                  <div className="flex items-center gap-2">
                    {incomingBids.filter(b => b.status === 'pending').length > 0 && (
                      <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 font-mono text-[10px] animate-pulse">
                        {incomingBids.filter(b => b.status === 'pending').length} needs review
                      </Badge>
                    )}
                  </div>
                </div>
                {incomingBids.length === 0 ? (
                  <div className="text-center py-6 text-gray-600"><DollarSign className="w-6 h-6 mx-auto mb-2 opacity-30" /><p className="text-sm">No bids yet</p></div>
                ) : (
                  <div className="space-y-2">
                    {incomingBids.filter(b => b.status === 'pending').slice(0, 3).map(b => (
                      <div key={b.id} className="flex items-center justify-between p-3 bg-yellow-500/[0.03] rounded border border-yellow-500/10">
                        <div><p className="text-sm text-white font-medium">{b.brandName}</p><p className="text-xs text-gray-500 font-mono">{b.slotDuration || 15}s &middot; ${b.bidCpm?.toFixed(2)} CPM</p></div>
                        <StatusBadge status="pending" />
                      </div>
                    ))}
                    {incomingBids.filter(b => b.status === 'approved').slice(0, 2).map(b => (
                      <div key={b.id} className="flex items-center justify-between p-3 bg-white/[0.02] rounded border border-white/5">
                        <div><p className="text-sm text-white">{b.brandName}</p><p className="text-xs text-gray-500 font-mono">${b.bidCpm?.toFixed(2)} CPM</p></div>
                        <StatusBadge status="approved" />
                      </div>
                    ))}
                  </div>
                )}
                <Button data-testid="view-all-bids" onClick={() => navigate('/streamer/bids')} variant="ghost"
                  className="w-full mt-3 text-gray-400 hover:text-white hover:bg-white/5 text-sm border border-white/5">
                  {incomingBids.filter(b => b.status === 'pending').length > 0 ? 'Review Bids' : 'View All Bids'} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* Session History */}
              <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-base font-bold text-white">Ad Break History</h3>
                  <span className="text-xs text-gray-500 font-mono">{breakHistory.length} breaks</span>
                </div>
                {breakHistory.length === 0 ? (
                  <div className="text-center py-6 text-gray-600"><Film className="w-6 h-6 mx-auto mb-2 opacity-30" /><p className="text-sm">No ad breaks run yet</p></div>
                ) : (
                  <div className="space-y-2">
                    {breakHistory.slice(0, 5).map(br => (
                      <div key={br.id} className="flex items-center justify-between p-3 bg-white/[0.02] rounded border border-white/5">
                        <div>
                          <p className="text-sm text-white">{br.totalAds} ads &middot; {br.completedAds} completed</p>
                          <p className="text-xs text-gray-500 font-mono">{new Date(br.startedAt).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {br.totalEarnings > 0 && <span className="text-sm font-bold text-emerald-400 font-mono">${br.totalEarnings.toFixed(4)}</span>}
                          <StatusBadge status={br.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: OBS Setup + Quick Links + Profile Analytics */}
            <div className="space-y-6">
              {/* OBS Setup Guide */}
              <div className="border border-white/10 rounded-lg bg-[#121212]/50 overflow-hidden">
                <div className="p-5 border-b border-white/[0.06]">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-emerald-400" />
                      <h3 className="font-heading text-base font-bold text-white uppercase">OBS Setup</h3>
                    </div>
                    <Badge data-testid="overlay-connection-status" className={`font-mono text-[10px] ${wsConnected ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                      {wsConnected ? 'Overlay Connected' : 'Not Connected'}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-gray-500">Follow these steps to connect AYO Source to your stream</p>
                </div>

                {/* Step-by-step guide */}
                <div className="p-5 space-y-4">
                  {[
                    { step: 1, title: 'Open OBS Studio', desc: 'Launch OBS (or Streamlabs/XSplit). Make sure your game capture and camera are already set up.' },
                    { step: 2, title: 'Add a Browser Source', desc: 'In the Sources panel, click the + button → select "Browser".' },
                    { step: 3, title: 'Paste the AYO URL', desc: 'Name it "AYO Source" and paste the URL below.' },
                    { step: 4, title: 'Set dimensions to 1920 x 1080', desc: 'Match your canvas size. This ensures the ad fills the full screen when triggered.' },
                    { step: 5, title: 'Move to TOP of source list', desc: 'Drag "AYO Source" to the very top of your Sources list. This is critical — it must render above your game and camera.' },
                    { step: 6, title: 'Enable audio', desc: 'Check "Control audio via OBS" in the browser source properties so ad audio plays through your stream.' },
                  ].map(s => (
                    <div key={s.step} className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[10px] font-mono font-bold text-gray-400">{s.step}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-white font-medium">{s.title}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5">{s.desc}</p>
                        {s.step === 3 && null}
                      </div>
                    </div>
                  ))}

                  {/* URL field — full width outside the step indent */}
                  <div className="flex items-center gap-2 mt-2 ml-9">
                    <code data-testid="obs-url" className="flex-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-xs font-mono text-emerald-400 select-all break-all">{obsUrl}</code>
                    <Button data-testid="copy-obs-url" size="sm" variant="ghost" className="text-gray-400 hover:text-white border border-white/10 h-8 w-8 p-0 flex-shrink-0"
                      onClick={() => { navigator.clipboard.writeText(obsUrl); toast.success('Copied!'); }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Test Button */}
                <div className="p-5 border-t border-white/[0.06] bg-[#0a0a0a]">
                  <Button data-testid="test-overlay-button" onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/ads/test-overlay`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      if (res.data.wsClients > 0) {
                        toast.success('Test pattern sent! Check your OBS preview.');
                      } else {
                        toast.error('No overlay connected. Make sure the Browser Source is active in OBS.');
                      }
                    } catch { toast.error('Failed to send test'); }
                  }} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs">
                    <Zap className="w-3.5 h-3.5 mr-2" /> Send Test Pattern to OBS
                  </Button>
                  <p className="text-[9px] text-gray-600 font-mono text-center mt-2">Sends a 3-second color bar test to verify connection</p>
                </div>
              </div>

              {/* Quick Links */}
              <div className="space-y-2">
                {[
                  { label: 'Stream Console', desc: 'Trigger ads live', icon: Play, path: '/streamer', color: '#FF3B30' },
                  { label: 'Manage Bids', desc: `${incomingBids.filter(b => b.status === 'pending').length || 'No'} bids awaiting review`, icon: DollarSign, path: '/streamer/bids', color: '#7C3AED' },
                  { label: 'Edit Profile', desc: 'Update your stats', icon: UserCircle, path: '/streamer/profile', color: '#3B82F6' },
                  { label: 'Earnings', desc: 'View revenue & payouts', icon: DollarSign, path: '/streamer/earnings', color: '#F59E0B' },
                ].map((link, i) => (
                  <button
                    key={i}
                    data-testid={`nav-${link.path.replace(/\//g, '-').slice(1)}`}
                    onClick={() => navigate(link.path)}
                    className="w-full flex items-center gap-4 p-4 border border-white/10 rounded-lg bg-[#121212]/50 hover:border-white/20 hover:bg-[#1E1E1E]/60 transition-all text-left group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${link.color}15` }}>
                      <link.icon className="w-5 h-5" style={{ color: link.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{link.label}</p>
                      <p className="text-xs text-gray-500">{link.desc}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-300 transition-colors" />
                  </button>
                ))}
              </div>

              {/* Floor CPM */}
              {profile?.floorCpm && (
                <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5 text-center">
                  <p className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-1">Your Floor CPM</p>
                  <p className="text-3xl font-black text-[#FF3B30] font-mono">${profile.floorCpm.toFixed(2)}</p>
                  <p className="text-xs text-gray-600 font-mono mt-1">Based on {(profile.avgCCV || 0).toLocaleString()} avg CCV (self-reported)</p>
                </div>
              )}

              {/* Live CCV Tracking */}
              <div className="border border-white/10 rounded-lg bg-[#121212]/50 overflow-hidden">
                <div className="p-5 border-b border-white/[0.06]">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-heading text-sm font-bold text-white uppercase flex items-center gap-2">
                      <Signal className="w-4 h-4 text-emerald-400" /> Live Viewer Count
                    </h4>
                    {ccvData?.isLive && (
                      <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase">Live</span>
                      </span>
                    )}
                  </div>

                  {/* CCV Comparison */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-3 bg-white/[0.02] rounded border border-white/5 text-center">
                      <p className="text-[9px] text-gray-500 font-mono uppercase mb-1">Self-Reported</p>
                      <p className="text-xl font-bold text-white font-mono">{(profile?.avgCCV || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-white/[0.02] rounded border border-emerald-500/20 text-center">
                      <p className="text-[9px] text-emerald-400 font-mono uppercase mb-1">
                        {ccvData?.isLive ? 'Live Now' : 'Verified'}
                      </p>
                      <p className={`text-xl font-bold font-mono ${ccvData?.liveCcv ? 'text-emerald-400' : 'text-gray-600'}`}>
                        {ccvData?.liveCcv ? ccvData.liveCcv.toLocaleString() : '---'}
                      </p>
                    </div>
                  </div>
                  {ccvData?.lastCheck && (
                    <p className="text-[9px] text-gray-600 font-mono">Last checked: {new Date(ccvData.lastCheck).toLocaleTimeString()}</p>
                  )}
                </div>

                {/* Linked Platforms */}
                <div className="p-5 border-b border-white/[0.06]">
                  <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-3">Linked Platforms</p>
                  {(ccvData?.channels || []).length > 0 ? (
                    <div className="space-y-2">
                      {ccvData.channels.map((ch, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-white/[0.02] rounded border border-white/5">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${ch.platform === 'kick' ? 'bg-emerald-400' : ch.platform === 'twitch' ? 'bg-purple-400' : 'bg-red-400'}`} />
                            <span className="text-xs font-mono text-white capitalize">{ch.platform}</span>
                            <span className="text-xs font-mono text-gray-500">{ch.displayName || ch.channelId}</span>
                          </div>
                          <button
                            onClick={async () => {
                              await axios.delete(`${API}/ccv/platforms/${ch.platform}`, { headers });
                              fetchAll();
                              toast.success(`${ch.platform} unlinked`);
                            }}
                            className="text-[9px] font-mono text-gray-600 hover:text-red-400 uppercase"
                          >remove</button>
                        </div>
                      ))}
                      <button
                        data-testid="poll-ccv-btn"
                        onClick={async () => {
                          try {
                            const r = await axios.post(`${API}/ccv/poll`, {}, { headers });
                            fetchAll();
                            const live = r.data.readings?.filter(x => x.isLive);
                            toast.success(live?.length ? `Live: ${live.map(x => `${x.platform} ${x.ccv.toLocaleString()} CCV`).join(', ')}` : 'Not currently live on any platform');
                          } catch { toast.error('Poll failed'); }
                        }}
                        className="w-full mt-2 py-1.5 border border-emerald-500/30 rounded text-[10px] font-mono font-bold text-emerald-400 uppercase hover:bg-emerald-500/10 transition-colors"
                      >
                        <Signal className="w-3 h-3 inline mr-1" /> Check CCV Now
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 font-mono">No platforms linked yet</p>
                  )}
                </div>

                {/* Add Platform */}
                <div className="p-5">
                  <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-3">Connect a Platform</p>
                  <div className="flex gap-2 mb-2">
                    {['kick', 'twitch', 'youtube'].map(p => (
                      <button
                        key={p}
                        data-testid={`link-${p}-btn`}
                        onClick={() => setLinkingPlatform(linkingPlatform === p ? '' : p)}
                        className={`flex-1 py-1.5 rounded text-[10px] font-mono font-bold uppercase border transition-all ${
                          linkingPlatform === p
                            ? 'border-white/30 bg-white/10 text-white'
                            : 'border-white/10 text-gray-500 hover:border-white/20 hover:text-gray-300'
                        }`}
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${p === 'kick' ? 'bg-emerald-400' : p === 'twitch' ? 'bg-purple-400' : 'bg-red-400'}`} />
                        {p}
                      </button>
                    ))}
                  </div>
                  {linkingPlatform && (
                    <div className="flex gap-2 mt-2">
                      <input
                        data-testid="platform-channel-input"
                        type="text"
                        value={linkingChannelId}
                        onChange={e => setLinkingChannelId(e.target.value)}
                        placeholder={linkingPlatform === 'kick' ? 'Channel slug (e.g. xqc)' : linkingPlatform === 'twitch' ? 'Channel login (e.g. shroud)' : 'Channel ID (UC...) or @handle'}
                        className="flex-1 bg-black/40 border border-white/10 rounded px-3 py-1.5 text-xs font-mono text-white placeholder:text-gray-600 focus:border-white/30 focus:outline-none"
                      />
                      <Button
                        data-testid="link-platform-submit"
                        size="sm"
                        disabled={!linkingChannelId.trim()}
                        onClick={async () => {
                          const existing = (ccvData?.channels || []).filter(ch => ch.platform !== linkingPlatform);
                          const channels = [...existing, { platform: linkingPlatform, channelId: linkingChannelId.trim() }];
                          try {
                            await axios.put(`${API}/ccv/platforms`, { channels }, { headers });
                            setLinkingPlatform('');
                            setLinkingChannelId('');
                            fetchAll();
                            toast.success(`${linkingPlatform} linked!`);
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed to link'); }
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold"
                      >Link</Button>
                    </div>
                  )}
                  <p className="text-[9px] text-gray-600 font-mono mt-2">All three platforms are live. Link your channels to start tracking verified CCV.</p>
                </div>
              </div>

              {/* Profile Analytics */}
              {profileAnalytics && (
                <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
                  <h4 className="font-heading text-sm font-bold text-white uppercase mb-3">Profile Analytics</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-white/[0.02] rounded border border-white/5 text-center">
                      <p className="text-lg font-bold text-white font-mono">{profileAnalytics.views}</p>
                      <p className="text-[9px] text-gray-600 font-mono uppercase">Profile Views</p>
                    </div>
                    <div className="p-2 bg-white/[0.02] rounded border border-white/5 text-center">
                      <p className="text-lg font-bold text-white font-mono">{profileAnalytics.totalBids}</p>
                      <p className="text-[9px] text-gray-600 font-mono uppercase">Total Bids</p>
                    </div>
                    <div className="p-2 bg-white/[0.02] rounded border border-white/5 text-center">
                      <p className="text-lg font-bold text-[#7C3AED] font-mono">{profileAnalytics.activeBids}</p>
                      <p className="text-[9px] text-gray-600 font-mono uppercase">Active Bids</p>
                    </div>
                    <div className="p-2 bg-white/[0.02] rounded border border-white/5 text-center">
                      <p className="text-lg font-bold text-emerald-400 font-mono">{profileAnalytics.conversionRate}%</p>
                      <p className="text-[9px] text-gray-600 font-mono uppercase">Bid Rate</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
